import { ErreurApi, type Client } from '../../api/client.js';
import { ROUTES, type Session } from '../../api/contrat.js';
import { avertissementSuppression, evaluerAction, motifValide, verifierIncarnation } from './modele.js';
import {
  ErreurSaisie,
  chargerAdministration,
  confirmerAction,
  creerEtude,
  demarrerIncarnation,
  supprimerEtude,
  terminerIncarnation,
  versionnerParametre,
} from './service.js';
import type { DonneesAdmin, Etude, EtudeSaisie, ParametreSaisie } from './types.js';

export type ModeSession = 'read' | 'act';

/** Une écriture faite « en tant que » (type `action`) ou une suppression d'étude faite sous son propre nom. */
export type Confirmation =
  | { type: 'action'; action: string; libelle?: string; avertissement?: string | null; payload?: unknown }
  | { type: 'suppression-etude'; etude: Etude };

export interface EtatAdmin {
  chargement: 'init' | 'en-cours' | 'pret' | 'erreur';
  donnees: DonneesAdmin | null;
  session: Session | null;
  message: string | null;
  selection: string | null;
  motif: string;
  confirmation: Confirmation | null;
  /** Absent des états antérieurs au choix du mode : lu comme « read ». */
  modeChoisi?: ModeSession;
  derniereConfirmation: string | null;
  occupe: boolean;
  /** Faux quand l'erreur est un refus de droit : réessayer ne changerait rien (PER-1-04). Absent : vrai. */
  reessayable?: boolean;
}

export interface OptionsControleurAdmin {
  client: Client;
  /** Relit la session de la coquille (bandeau « voir en tant que ») après un début ou une fin de session. */
  recharger: () => void;
  /** Appelé après la création réussie d'une étude : l'écran y pose la question BETA-26 (lot 4, point 8 b). */
  apresCreationEtude?: () => void;
}

/** La confirmation porte sur la PERMISSION de la route (ce que le serveur compare), pas sur son identifiant. */
const permissionDe = (action: string): string => ROUTES.find((r) => r.id === action)?.permission ?? action;

const ECHEC = 'L’opération n’a pas abouti. Rien n’a été modifié.';

/**
 * Parcours de l'écran D10 : démarrer, voir, tenter une action (confirmation exigée), terminer. Aucune écriture
 * destructive ou faite en tant que ne part avant l'appel explicite à `confirmer`.
 */
export function creerControleurAdmin({ client, recharger, apresCreationEtude }: OptionsControleurAdmin) {
  let etat: EtatAdmin = {
    chargement: 'init',
    donnees: null,
    session: null,
    message: null,
    selection: null,
    motif: '',
    confirmation: null,
    modeChoisi: 'read',
    derniereConfirmation: null,
    occupe: false,
  };
  /** L'écriture réelle suspendue derrière la boîte de confirmation ; elle ne part qu'après `impersonation.confirmer`, avec son identifiant. */
  let enAttente: ((confirmationId: string) => Promise<unknown>) | null = null;
  const abonnes = new Set<() => void>();
  const modifier = (changements: Partial<EtatAdmin>): void => {
    etat = { ...etat, ...changements };
    for (const ecoute of [...abonnes]) ecoute();
  };

  async function recharge(): Promise<void> {
    modifier({ donnees: await chargerAdministration(client) });
  }

  /** Exécute une écriture, recharge les données, et traduit toute panne en un message sans détail technique. */
  async function ecrire(succes: string, travail: () => Promise<unknown>): Promise<boolean> {
    modifier({ occupe: true, message: null });
    try {
      await travail();
      await recharge();
      modifier({ occupe: false, message: succes });
      return true;
    } catch (e) {
      modifier({ occupe: false, message: e instanceof ErreurApi || e instanceof ErreurSaisie ? e.message : ECHEC });
      return false;
    }
  }

  /**
   * Toute écriture faite « en tant que » : refusée en lecture seule, suspendue derrière la confirmation en mode
   * actions tracées. `action` est l'identifiant de la route ; `payload` le corps réel de l'écriture (absent pour un DELETE).
   * `executer` est l'écriture réelle : elle ne part qu'une fois la confirmation journalisée, avec l'identifiant reçu.
   */
  function tenterAction(
    action: string,
    payload?: unknown,
    executer?: (confirmationId: string) => Promise<unknown>,
    libelle: string = action,
    avertissement: string | null = null,
  ): void {
    const courante = etat.session?.impersonation ?? null;
    if (!courante) return modifier({ message: 'Aucune session « voir en tant que » en cours.' });
    const verdict = evaluerAction(courante.mode);
    if (verdict.type === 'refusee') return modifier({ message: verdict.raison });
    enAttente = executer ?? null;
    modifier({ confirmation: { type: 'action', action, libelle, avertissement, payload }, message: null });
  }

  return {
    subscribe: (ecoute: () => void): (() => void) => {
      abonnes.add(ecoute);
      return () => {
        abonnes.delete(ecoute);
      };
    },
    getEtat: (): EtatAdmin => etat,

    async charger(): Promise<void> {
      modifier({ chargement: 'en-cours', message: null });
      try {
        const session = await client.appeler<Session>('auth.moi');
        const donnees = await chargerAdministration(client);
        modifier({ chargement: 'pret', session, donnees });
      } catch (erreur) {
        if (erreur instanceof ErreurApi && erreur.status === 403)
          modifier({
            chargement: 'erreur',
            message: 'L’administration est réservée aux comptes administrateurs du Cabinet MAAT.',
            reessayable: false,
          });
        else modifier({ chargement: 'erreur', message: 'Impossible de charger l’administration. Réessayez dans un instant.', reessayable: true });
      }
    },

    choisir(id: string): void {
      modifier({ selection: id, message: null });
    },

    saisirMotif(motif: string): void {
      modifier({ motif });
    },

    /** Choix explicite du mode de la session : lecture seule par défaut, actions tracées sur demande. */
    choisirMode(mode: ModeSession): void {
      modifier({ modeChoisi: mode });
    },

    async demarrer(): Promise<void> {
      const cible = etat.donnees?.utilisateurs.find((u) => u.id === etat.selection);
      if (!cible || !etat.session) return modifier({ message: 'Choisissez d’abord une personne dans la liste.' });
      const verdict = verifierIncarnation(etat.session.user, cible, etat.session.impersonation !== null);
      if (!verdict.ok) return modifier({ message: verdict.raison });
      if (!motifValide(etat.motif)) {
        return modifier({ message: 'Indiquez le motif de la consultation (trois caractères au moins).' });
      }
      modifier({ occupe: true, message: null });
      try {
        await demarrerIncarnation(client, cible.id, etat.motif, etat.modeChoisi ?? 'read');
        modifier({ occupe: false, motif: '', selection: null, modeChoisi: 'read' });
        recharger();
      } catch (e) {
        modifier({ occupe: false, message: e instanceof ErreurApi ? e.message : ECHEC });
      }
    },

    async terminer(): Promise<void> {
      const courante = etat.session?.impersonation;
      if (!courante) return;
      modifier({ occupe: true, message: null });
      try {
        await terminerIncarnation(client, courante.id);
        modifier({ occupe: false });
        recharger();
      } catch (e) {
        modifier({ occupe: false, message: e instanceof ErreurApi ? e.message : ECHEC });
      }
    },

    tenterAction,

    demanderSuppressionEtude(etudeId: string): void {
      const etude = etat.donnees?.etudes.find((e) => e.id === etudeId);
      if (!etude) return;
      const courante = etat.session?.impersonation ?? null;
      if (courante) {
        return tenterAction(
          'admin.etudes.supprimer',
          undefined,
          (confirmationId) => supprimerEtude(client, etude.id, confirmationId),
          `Supprimer l’étude « ${etude.name} »`,
          avertissementSuppression(etude),
        );
      }
      modifier({ confirmation: { type: 'suppression-etude', etude }, message: null });
    },

    annuler(): void {
      enAttente = null;
      modifier({ confirmation: null });
    },

    async confirmer(): Promise<void> {
      const confirmation = etat.confirmation;
      if (!confirmation) return;
      const ecriture = enAttente;
      enAttente = null;
      modifier({ confirmation: null });
      if (confirmation.type === 'suppression-etude') {
        await ecrire('L’étude a été supprimée.', () => supprimerEtude(client, confirmation.etude.id));
        return;
      }
      const courante = etat.session?.impersonation;
      if (!courante) return;
      modifier({ occupe: true, message: null, derniereConfirmation: null });
      try {
        const reponse = await confirmerAction(client, courante.id, permissionDe(confirmation.action), confirmation.payload);
        if (ecriture) {
          await ecriture(reponse.confirmationId);
          await recharge();
        }
        modifier({
          derniereConfirmation: reponse.confirmationId,
          occupe: false,
          message: `Action confirmée et journalisée : faite par ${etat.session?.user.displayName ?? 'vous'} en tant que ${courante.targetDisplayName}.`,
        });
      } catch (e) {
        modifier({ occupe: false, message: e instanceof ErreurApi || e instanceof ErreurSaisie ? e.message : ECHEC });
      }
    },

    async ajouterEtude(saisie: EtudeSaisie): Promise<void> {
      if (etat.session?.impersonation) {
        return tenterAction(
          'admin.etudes.creer',
          saisie,
          (confirmationId) => creerEtude(client, saisie, confirmationId),
          `Ajouter l’étude « ${saisie.name} »`,
        );
      }
      if (await ecrire('L’étude a été ajoutée.', () => creerEtude(client, saisie))) apresCreationEtude?.();
    },

    async versionner(saisie: ParametreSaisie): Promise<void> {
      if (etat.session?.impersonation) {
        return tenterAction(
          'admin.parametresLegaux.versionner',
          saisie,
          (confirmationId) => versionnerParametre(client, saisie, confirmationId),
          'Enregistrer un nouveau seuil daté',
        );
      }
      await ecrire('Le seuil a été enregistré avec sa date d’effet.', () => versionnerParametre(client, saisie));
    },
  };
}

export type ControleurAdmin = ReturnType<typeof creerControleurAdmin>;
